import { stringify } from 'query-string';
import { fetchUtils } from 'ra-core';
require('dotenv').config();

const dataProvider = (apiUrl, httpClient = fetchUtils.fetchJson) => ({
    getList: (resource, params) => {
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;
        const query = {
            sort: JSON.stringify([field, order]),
            filters: JSON.stringify(params.filter),
            pagination: JSON.stringify([perPage, page])
        };

        var func = params.func
        if(func) {
            resource = resource.concat('/'+func)
        }

        const url = `${apiUrl}/${resource}?${stringify(query)}`;
        return httpClient(url).then(({ headers, json }) => {
            if (!headers.has('content-range')) {
                throw new Error(
                    'The Content-Range header is missing in the HTTP Response. The simple REST data provider expects responses for lists of resources to contain this header with the total number of results to build the pagination. If you are using CORS, did you declare Content-Range in the Access-Control-Expose-Headers header?'
                );
            }
            return {
                data: func ? json[func] : json[resource],
                total: parseInt(
                    headers.get('content-range').split('/').pop(),
                    10
                )
            };
        });
    },

    getOne: (resource, params) => 
        httpClient(`${apiUrl}/${resource}/${params.id}`).then(({ json }) => ({
            data: json
        })),

    getMany: (resource, params) => {
        const query = {
            filters: JSON.stringify({ id: params.ids })
        };
        const url = `${apiUrl}/${resource}?${stringify(query)}`;
        return httpClient(url).then(({ json }) => ({ data: json[resource] }));
    },

    getManyReference: (resource, params) => {
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;
        const query = {
            sort: JSON.stringify([field, order]),
            filter: JSON.stringify({
                ...params.filter,
                [params.target]: params.id
            }),
            pagination: JSON.stringify([perPage, page])
        };
        const url = `${apiUrl}/${resource}?${stringify(query)}`;

        return httpClient(url).then(({ headers, json }) => {
            if (!headers.has('content-range')) {
                throw new Error(
                    'The Content-Range header is missing in the HTTP Response. The simple REST data provider expects responses for lists of resources to contain this header with the total number of results to build the pagination. If you are using CORS, did you declare Content-Range in the Access-Control-Expose-Headers header?'
                );
            }
            return {
                data: json,
                total: parseInt(
                    headers.get('content-range').split('/').pop(),
                    10
                )
            };
        });
    },

    update: (resource, params) => {
        return httpClient(`${apiUrl}/${resource}/${params.id}`, {
            method: 'PUT',
            body: JSON.stringify(params.data)
        }).then(({ json }) => ({ data: json }))
    },

    // simple-rest doesn't handle provide an updateMany route, so we fallback to calling update n times instead
    updateMany: (resource, params) =>
        Promise.all(
            params.ids.map((id) =>
                httpClient(`${apiUrl}/${resource}/${id}`, {
                    method: 'PUT',
                    body: JSON.stringify(params.data)
                })
            )
        ).then((responses) => ({ data: responses.map(({ json }) => json.id) })),

    create: (resource, params) =>
        httpClient(`${apiUrl}/${resource}`, {
            method: 'POST',
            body: JSON.stringify(params.data)
        }).then(({ json }) => ({
            data: { ...params.data, id: json.id }
        })),

    budgetUpdate: (params) => {
        return httpClient(`${apiUrl}/budget/update`, {
            method: 'POST',
            body: JSON.stringify(params.data)
        }).then(({ json }) => ({ data: json }))
    },

    delete: (resource, params) =>
        httpClient(`${apiUrl}/${resource}/${params.id}`, {
            method: 'DELETE'
        }).then(({ json }) => ({ data: json })),

    // simple-rest doesn't handle filters on DELETE route, so we fallback to calling DELETE n times instead
    deleteMany: (resource, params) =>
        Promise.all(
            params.ids.map((id) =>
                httpClient(`${apiUrl}/${resource}/${id}`, {
                    method: 'DELETE'
                })
            )
        ).then((responses) => ({ data: responses.map(({ json }) => json.id) }))
});

const httpClient = (url, options = {}) => {
    if (!options.headers) {
        options.headers = new Headers({ Accept: 'application/json' });
    }
    const token = localStorage.getItem('token');
    options.headers.set('Authorization', 'Bearer '+token);
    options.credentials = `${process.env.REACT_APP_HTTP_CREDENTIALS || 'same-origin'}`;
    return fetchUtils.fetchJson(url, options);
};

export default dataProvider(
    `${process.env.REACT_APP_API_URL || ''}/api`,
    httpClient
);
